import { FC, useState, useMemo, Dispatch, SetStateAction, ChangeEvent, useEffect } from 'react'
import {
  Box,
  InputAdornment,
  Typography,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  AccordionSummary,
  AccordionDetails,
  Skeleton
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import theme from 'theme'
import dayjs from 'dayjs'

/* Utils */
import {
  pricingUnitsOptions,
  getPeriodDateRange,
  calculateContractPeriods,
  NumericFormatCustom
} from './add-product.utils'
import { SSMResponseProduct, SSMAssetType, SSMNistPillar } from 'models'
import { getUTCYear, isCurrentCell } from '../ssm.utils'
import { CostDistribution } from '../../securityStackMap.utils'
import { getOrdinalWord } from 'lib/utils'

/* Components */
import { ChipTooltip } from '../../../../components/tooltip/ChipTooltip'
import TextField from '../../../../components/form-components/TextField'
import { AddProductFormWrapper, LicenseDetailsAccordion, SumNotMatchedWrapper } from './addProductForm.styles'
import { ProductSkeleton } from '../ssmDrawer.styles'
import SingleDatePicker from '../../../../components/single-date-picker'
import SingleSelectField from '../../../../components/form-components/SingleSelectField'
import { FormData } from '../SSMDrawer'

interface Props {
  formData: FormData
  setFormData: Dispatch<SetStateAction<FormData>>
  productInstances: any[]
  asset: SSMResponseProduct
  editingProduct?: boolean
  costDistribution?: CostDistribution
  setCostDistribution?: Dispatch<SetStateAction<CostDistribution>>
  expenseCalculationFields?: any[]
  setExpenseCalculationFields?: Dispatch<SetStateAction<any[]>>
  currentNist?: SSMNistPillar | null
  currentAsset?: SSMAssetType | null
  contractYear?: string
  isNewProduct?: boolean
  loading?: boolean
}

const AddProductForm: FC<Props> = ({
  formData,
  setFormData,
  productInstances,
  editingProduct,
  costDistribution,
  setCostDistribution,
  expenseCalculationFields,
  setExpenseCalculationFields,
  currentNist,
  currentAsset,
  isNewProduct,
  loading
}) => {
  const [isMultiYear, setIsMultiYear] = useState(false)
  const [licenseDetailsExpanded, setLicenseDetailsExpanded] = useState(!editingProduct)

  // Check if perYear has different values across years and set isMultiYear accordingly when editing or adding existing product
  useEffect(() => {
    // Run when editing OR when adding an existing product (not a new product)
    if ((editingProduct || !isNewProduct) && formData.perYear.length > 1) {
      const firstEntry = formData.perYear[0]
      const hasDifferentValues = formData.perYear.some(
        (entry) =>
          entry.licenseCost !== firstEntry.licenseCost || entry.numberOfLicenses !== firstEntry.numberOfLicenses
      )
      if (hasDifferentValues) {
        setIsMultiYear(true)
      }
    }
  }, [editingProduct, isNewProduct, formData.perYear])

  // Sync perYear with contractGroups when dates change
  useEffect(() => {
    if (!formData.startDate || !formData.endDate) return

    const startYear = getUTCYear(formData.startDate)
    const numPeriods = calculateContractPeriods(formData.startDate, formData.endDate)
    const expectedYears = Array.from({ length: numPeriods }, (_, i) => startYear + i)

    // Check if perYear already has the correct years
    const currentYears = formData.perYear.map((p) => p.year).sort((a, b) => a - b)
    const yearsMatch =
      currentYears.length === expectedYears.length && expectedYears.every((year, i) => currentYears[i] === year)

    if (!yearsMatch) {
      const firstEntry = formData.perYear[0] || { licenseCost: 0, numberOfLicenses: 0 }
      setFormData((prev) => ({
        ...prev,
        perYear: expectedYears.map((year) => {
          const existing = prev.perYear.find((p) => p.year === year)
          return {
            licenseCost: existing?.licenseCost ?? firstEntry.licenseCost,
            numberOfLicenses: existing?.numberOfLicenses ?? firstEntry.numberOfLicenses,
            year
          }
        })
      }))
    }
  }, [formData.startDate, formData.endDate])

  const annualLicenseCost = useMemo(() => {
    if (formData.perYear.length === 0) return 0
    return formData.perYear.reduce((acc, item) => acc + (item.licenseCost ?? 0), 0)
  }, [formData.perYear])

  const handleChangeCalculationFields = (index: number, event: any) => {
    const value = event.target.value === '' ? undefined : Math.max(0, parseFloat(event.target.value) || 0)
    if (!setExpenseCalculationFields || !expenseCalculationFields) return

    const values = [...expenseCalculationFields]
    values[index].value = value
    setExpenseCalculationFields(values)
  }

  const fieldsTotalCost = useMemo(
    () => Math.round((expenseCalculationFields?.reduce((acc, obj) => acc + (obj.value ?? 0), 0) || 0) * 100) / 100,
    [expenseCalculationFields]
  )

  const sumNotMatchedWarning = useMemo(() => {
    // Only show warning for manual distribution
    if (costDistribution !== CostDistribution.Manual) return false

    const fieldsCost = fieldsTotalCost

    if (fieldsCost === 0) return false
    return expenseCalculationFields && expenseCalculationFields.length > 1 && fieldsCost !== 100
  }, [fieldsTotalCost, expenseCalculationFields, costDistribution])

  const contractGroups = useMemo(() => {
    if (!formData.startDate || !formData.endDate) return []

    const startYear = getUTCYear(formData.startDate)
    const numPeriods = calculateContractPeriods(formData.startDate, formData.endDate)

    return Array.from({ length: numPeriods }, (_, index) => ({
      licenseCost: 0,
      numberOfLicenses: 0,
      year: startYear + index
    }))
  }, [formData.startDate, formData.endDate])

  const singleGroup = useMemo(() => {
    const year = formData.startDate ? getUTCYear(formData.startDate) : new Date().getUTCFullYear()
    return {
      licenseCost: 0,
      numberOfLicenses: 0,
      year
    }
  }, [formData.startDate])

  const handleToggleYearMode = () => {
    setIsMultiYear((prev) => {
      if (prev) {
        const firstEntry = formData.perYear[0] || {
          licenseCost: 0,
          numberOfLicenses: 0,
          year: singleGroup.year
        }
        setFormData((prevForm) => ({
          ...prevForm,
          perYear: [
            {
              licenseCost: firstEntry.licenseCost,
              numberOfLicenses: firstEntry.numberOfLicenses,
              year: singleGroup.year
            }
          ]
        }))
      } else {
        setFormData((prevForm) => {
          const firstEntry = prevForm.perYear[0] || {
            licenseCost: 0,
            numberOfLicenses: 0,
            year: singleGroup.year
          }
          const newPerYear = contractGroups.map((group) => {
            const existingEntry = prevForm.perYear.find((entry) => entry.year === group.year)
            return {
              licenseCost: existingEntry?.licenseCost ?? firstEntry.licenseCost,
              numberOfLicenses: existingEntry?.numberOfLicenses ?? firstEntry.numberOfLicenses,
              year: group.year
            }
          })
          return {
            ...prevForm,
            perYear: newPerYear
          }
        })
      }
      return !prev
    })
  }

  const licenseDetailsContent = (
    <>
      <Box className="dates-group">
        <SingleDatePicker
          value={formData.startDate ? dayjs(formData.startDate) : null}
          onChange={(e) => {
            const value = dayjs(e).format('YYYY-MM-DD')
            setFormData((prev) => ({
              ...prev,
              startDate: value
            }))
          }}
          label="Start Date"
          wrapperWidth="100%"
          minWidth="unset !important"
          inputWidth="100% !important"
          maxDate={formData.endDate ? dayjs(formData.endDate).subtract(1, 'day') : undefined}
        />
        <SingleDatePicker
          value={formData.endDate ? dayjs(formData.endDate) : null}
          onChange={(e) => {
            const value = dayjs(e).format('YYYY-MM-DD')
            setFormData((prev) => ({
              ...prev,
              endDate: value
            }))
          }}
          label="End Date"
          wrapperWidth="100%"
          inputWidth="100% !important"
          minWidth="unset !important"
          minDate={formData.startDate ? dayjs(formData.startDate).add(1, 'day') : undefined}
        />
      </Box>

      {formData.startDate && formData.endDate && (
        <>
          {(isMultiYear ? contractGroups : [singleGroup]).map((contract, index) => (
            <Box className="inputs-group" key={contract.year}>
              {formData.startDate && formData.endDate && (isMultiYear || contractGroups.length > 1) && (
                <Box className="group-title">
                  {isMultiYear ? (
                    <ChipTooltip
                      placement="bottom-start"
                      PopperProps={{
                        sx: {
                          marginTop: `-${theme.spacing.sm}px !important`
                        }
                      }}
                      title={
                        <Box className="tooltip-wrapper-permissions one-line">
                          <Typography className="tooltip-text">
                            {(() => {
                              const { periodStart, periodEnd } = getPeriodDateRange(
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                formData.startDate!,
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                formData.endDate!,
                                index
                              )
                              return `${periodStart} - ${periodEnd}`
                            })()}
                          </Typography>
                        </Box>
                      }
                    >
                      <Box className="group-title-content">
                        <Typography className="title">{`${contract.year}-${contract.year + 1}`}</Typography>
                        <Typography className="text">{getOrdinalWord(index)} year</Typography>
                      </Box>
                    </ChipTooltip>
                  ) : (
                    <Typography className="title">
                      {`${contractGroups[0].year}-${contractGroups[contractGroups.length - 1].year + 1}`}
                    </Typography>
                  )}
                </Box>
              )}

              <TextField
                name="licenseCost"
                type="number"
                fullWidth
                label="Annual License Cost"
                variant="outlined"
                value={formData.perYear.find((c) => c.year === contract.year)?.licenseCost ?? ''}
                InputProps={{
                  endAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: { min: 0 }
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))

                  setFormData((prev) => {
                    if (!isMultiYear && prev.startDate && prev.endDate) {
                      // In collapsed mode, apply the same value to all years in the range
                      const startYear = getUTCYear(prev.startDate)
                      const numPeriods = calculateContractPeriods(prev.startDate, prev.endDate)
                      const years = Array.from({ length: numPeriods }, (_, i) => startYear + i)
                      const firstEntry = prev.perYear[0] || { licenseCost: 0, numberOfLicenses: 0 }
                      return {
                        ...prev,
                        perYear: years.map((year) => {
                          const existing = prev.perYear.find((c) => c.year === year)
                          return {
                            licenseCost: value,
                            numberOfLicenses: existing?.numberOfLicenses ?? firstEntry.numberOfLicenses,
                            year
                          }
                        })
                      }
                    }

                    // In multi-year mode, update only the specific year
                    const perYear = [...prev.perYear]
                    const existingIndex = perYear.findIndex((c) => c.year === contract.year)

                    if (existingIndex >= 0) {
                      perYear[existingIndex] = {
                        ...perYear[existingIndex],
                        licenseCost: value
                      }
                    } else {
                      perYear.push({
                        licenseCost: value,
                        numberOfLicenses: contract.numberOfLicenses || 0,
                        year: contract.year
                      })
                    }

                    return {
                      ...prev,
                      perYear
                    }
                  })
                }}
              />

              <TextField
                name="numberOfLicenses"
                type="number"
                fullWidth
                label="Number of Licenses"
                variant="outlined"
                value={formData.perYear.find((c) => c.year === contract.year)?.numberOfLicenses ?? ''}
                InputProps={{
                  inputProps: { min: 0 }
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value === '' ? undefined : Math.max(0, Number(e.target.value))

                  setFormData((prev) => {
                    if (!isMultiYear && prev.startDate && prev.endDate) {
                      // In collapsed mode, apply the same value to all years in the range
                      const startYear = getUTCYear(prev.startDate)
                      const numPeriods = calculateContractPeriods(prev.startDate, prev.endDate)
                      const years = Array.from({ length: numPeriods }, (_, i) => startYear + i)
                      const firstEntry = prev.perYear[0] || { licenseCost: 0, numberOfLicenses: 0 }
                      return {
                        ...prev,
                        perYear: years.map((year) => {
                          const existing = prev.perYear.find((c) => c.year === year)
                          return {
                            licenseCost: existing?.licenseCost ?? firstEntry.licenseCost,
                            numberOfLicenses: value,
                            year
                          }
                        })
                      }
                    }

                    // In multi-year mode, update only the specific year
                    const perYear = [...prev.perYear]
                    const existingIndex = perYear.findIndex((c) => c.year === contract.year)

                    if (existingIndex >= 0) {
                      perYear[existingIndex] = {
                        ...perYear[existingIndex],
                        numberOfLicenses: value
                      }
                    } else {
                      perYear.push({
                        licenseCost: 0,
                        numberOfLicenses: value,
                        year: contract.year
                      })
                    }

                    return {
                      ...prev,
                      perYear
                    }
                  })
                }}
              />
            </Box>
          ))}

          {contractGroups.length > 1 && (
            <Box className="contract-groups-year-selector-wrapper">
              <Typography className="year-selector-text" onClick={handleToggleYearMode}>
                {isMultiYear ? 'Collapse to one value' : 'Specify each year'}
              </Typography>
            </Box>
          )}
        </>
      )}

      <SingleSelectField
        key={`pricing-units-${formData.pricingUnits || 'empty'}`}
        options={pricingUnitsOptions}
        placeholder="Select"
        label="Pricing Units"
        value={formData.pricingUnits}
        handleChange={(e: any) => {
          const value = e.target.value
          setFormData((prev) => ({
            ...prev,
            pricingUnits: value
          }))
        }}
        disabled={false}
        minWidth="144px !important"
      />
    </>
  )

  if (loading) {
    return (
      <AddProductFormWrapper>
        <Box className="license-details-wrapper">
          {Array.from(Array(4).keys()).map((key) => (
            <ProductSkeleton key={key}>
              <Skeleton
                variant="rectangular"
                component="div"
                animation="wave"
                width="100%"
                height="100%"
                className="card-skeleton-inner"
              />
            </ProductSkeleton>
          ))}
        </Box>
      </AddProductFormWrapper>
    )
  }
  return (
    <AddProductFormWrapper>
      {editingProduct ? (
        <LicenseDetailsAccordion
          expanded={licenseDetailsExpanded}
          onChange={(_, expanded) => setLicenseDetailsExpanded(expanded)}
          disableGutters
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
            backgroundColor: 'transparent'
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              padding: 0,
              minHeight: 'unset',
              '& .MuiAccordionSummary-content': { margin: 0 }
            }}
          >
            <Box className="add-product-header">License Details</Box>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>{licenseDetailsContent}</AccordionDetails>
        </LicenseDetailsAccordion>
      ) : (
        <Box className="license-details-wrapper">
          <Box className="add-product-header">License Details</Box>
          {licenseDetailsContent}
        </Box>
      )}

      {!isNewProduct && productInstances && productInstances.length > 1 && (
        <Box className="expense-calculation">
          <Typography className="cost-distribution-title">Cost Distribution</Typography>
          <FormControl fullWidth className="cost-distribution-form-control">
            <RadioGroup
              value={costDistribution}
              onChange={(e) => {
                if (!setCostDistribution) return
                setCostDistribution(e.target.value as CostDistribution)
              }}
              name="expense-calculation-group"
            >
              <FormControlLabel
                value="even"
                control={<Radio />}
                label={`Evenly distribute cost across cells (${
                  annualLicenseCost ? parseFloat((100 / productInstances.length).toFixed(2)) : 0
                }%${productInstances.length > 1 ? ' each' : ''})`}
              />
              <FormControlLabel value="manual" control={<Radio />} label="Edit each cell's cost manually" />
            </RadioGroup>
          </FormControl>

          {costDistribution === CostDistribution.Manual && expenseCalculationFields && (
            <Box sx={{ mt: 2 }}>
              {expenseCalculationFields.length > 0 &&
                expenseCalculationFields.map((field: any, index) => {
                  return (
                    <Box key={index} className="expense-calculation-field-wrapper">
                      <Box className="expense-calculation-field-item field-container">
                        <Box className="expense-calculation-left">
                          <Typography>
                            {field.nist}/{field.asset}
                          </Typography>

                          <Typography
                            className="current-area"
                            component="span"
                            sx={{
                              visibility: isCurrentCell(field.nist, field.asset, currentNist, currentAsset)
                                ? 'visible'
                                : 'hidden'
                            }}
                          >
                            Current area
                          </Typography>
                        </Box>
                      </Box>
                      <Box className="expense-calculation-field-item">
                        <TextField
                          variant="outlined"
                          label=""
                          value={field.value ?? ''}
                          onChange={(e: any) => {
                            handleChangeCalculationFields(index, e)
                          }}
                          name={field.name}
                          InputProps={{
                            inputComponent: NumericFormatCustom as any,
                            autoComplete: 'off',
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                          fullWidth
                        />
                      </Box>
                    </Box>
                  )
                })}
            </Box>
          )}

          {sumNotMatchedWarning && (
            <SumNotMatchedWrapper className="sum-not-matched-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M1 21.5H23L12 2.5L1 21.5ZM13 18.5H11V16.5H13V18.5ZM13 14.5H11V10.5H13V14.5Z" fill="black" />
              </svg>
              <Typography>The cells' cost total ({fieldsTotalCost}%) doesn't equal 100%</Typography>
            </SumNotMatchedWrapper>
          )}
        </Box>
      )}

      <Box className="note-wrapper">
        <Typography className="placement-note-title">Placement Note</Typography>
        <TextField
          name="note"
          fullWidth
          label="Note"
          placeholder="Why is this product added to SSM?"
          variant="outlined"
          value={formData.note || ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setFormData((prev) => ({
              ...prev,
              note: e.target.value
            }))
          }}
          counterLimit={200}
          multiline
        />
      </Box>
    </AddProductFormWrapper>
  )
}

export default AddProductForm
